'use client';

import { FC, useState } from 'react';

import { ElasticUtils } from '@ravago/shared/next-data-access/client';
import { AutoSuggestQueryV2Args, AutoSuggestV2Response, BasicFacetWithTermsV2 } from '@ravago/shared/page-data/models/elastic';
import { DynamicPageUrl, RendererGlobals } from '@ravago/shared/page-data/models/utils';

import { useQuery } from '@tanstack/react-query';

import { useRouter } from 'next/navigation';

import { CorrelationService } from '../../../../services/tracking/correlation-token.service';
import { getLanguageCode } from '../../../../utils/locale/locale.utils';
import { AutoSuggestV2 } from '../../../shared/auto-suggest/auto-suggest-v2';
import { AutoSuggestClientV2Request } from '../../product-portfolio/models/client-request.model';
import { basicFacetWithTermsToQueryV2 } from '../../product-portfolio/utils/filter-query-utils';

interface Props {
   baseRoutes?: {
      product: string;
      brand: string;
      caseStudy: string;
   };
   productOverviewRoute: DynamicPageUrl | null;
   locale: string;
   globals: RendererGlobals;
   countryId: string;
}

const AUTO_SUGGEST_SIZE = 70;

const getAutoSuggestResultV2 = async ({ search, correlationId, country, language }: AutoSuggestClientV2Request): Promise<AutoSuggestV2Response> => {
   const request: AutoSuggestQueryV2Args = {
      query: search.trim(),
      country,
      language,
      size: AUTO_SUGGEST_SIZE
   };

   return ElasticUtils.getAutosuggestV2Result(request, correlationId);
};

export const HomepageSearchV2: FC<Props> = ({ baseRoutes, productOverviewRoute, locale, globals, countryId }) => {
   const router = useRouter();
   const [search, setSearch] = useState('');
   const [autoSuggestExpanded, setAutoSuggestExpanded] = useState(false);

   const language = getLanguageCode(locale);

   const autoSuggestQuery = useQuery({
      queryKey: ['auto-suggest', { search, language, countryId }],
      queryFn: () =>
         getAutoSuggestResultV2({ language, search, correlationId: CorrelationService.generateCorrelationId('dpcid'), country: [countryId] }),
      enabled: () => search.length > 1 && autoSuggestExpanded
   });

   const handleSearch = (value?: string, facetFilters?: Array<BasicFacetWithTermsV2>) => {
      if (!productOverviewRoute?.url) return;

      const params =
         facetFilters && facetFilters.length > 0 ? new URLSearchParams(basicFacetWithTermsToQueryV2(facetFilters)) : new URLSearchParams();

      params.set('tab', 'products');

      if (value) params.set('search', value);

      router.push(`/${locale}/${productOverviewRoute.url}?${params.toString()}`);
   };

   return (
      <AutoSuggestV2
         id="global-search"
         language={language}
         result={autoSuggestQuery.data}
         loading={autoSuggestQuery.isFetching}
         baseRoutes={baseRoutes}
         globals={globals}
         onChange={setSearch}
         onSearch={handleSearch}
         onFacetsClick={(facets) => handleSearch(undefined, facets)}
         productOverviewRoute={productOverviewRoute ?? undefined}
         expanded={autoSuggestExpanded}
         onAutoSuggestMenuChange={(value) => setAutoSuggestExpanded(value)}
         facetType="apply"
      />
   );
};
